import { Delete, EditNote, UploadFileOutlined } from "@mui/icons-material";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { deleteCustomer, exportCustomers } from "../../../../../api/customers";
import AccountBulkEditDialog from "../../dialogs/accounts/accountBulkEditDialog";
import { assertSectionControllerContract } from "../../../../shared/management/sections/contracts/sectionControllerContract";
import BookkeeperMultiSelectPopover from "../../../../shared/management/sections/shared/table/BookkeeperMultiSelectPopover";
import MultiSelectSortFilterPopover from "../../../../shared/management/sections/shared/table/MultiSelectSortFilterPopover";
import RowActionsMenu from "../../../../shared/management/sections/shared/table/RowActionsMenu";
import ConfirmDeleteDialog from "../../../../shared/management/shared/ConfirmDeleteDialog";
import { createManagementCellRenderer } from "../../../../shared/management/sections/shared/table/createManagementCellRenderer";
import useAccountsTabData from "./useAccountsTabData";

const ACCOUNT_TYPE_FILTER_OPTIONS = [
  { label: "Trial", value: "Trial" },
  { label: "Onboarding", value: "Onboarding" },
  { label: "Ongoing", value: "Ongoing" },
  { label: "Consolidated", value: "Consolidated" },
  { label: "Aggregated", value: "Aggregated" },
  { label: "Demo", value: "Demo" },
];

export function useAccountsSectionController({
  profile,
  columns,
  rowsPerPage,
  sortConfig,
  onSortChange,
  selectedRows,
  setSelectedRows,
  openDialogManager,
  notify,
}) {
  const accountsTabData = useAccountsTabData({ profile, rowsPerPage, sortConfig });

  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deletingRow, setDeletingRow] = useState(false);

  const managerOptions = useMemo(() => {
    const values = new Set();
    for (const row of accountsTabData.data || []) {
      if (row?.manager) {
        values.add(String(row.manager).trim());
      }
    }
    return Array.from(values).sort();
  }, [accountsTabData.data]);

  const bookkeeperOptions = useMemo(() => {
    const values = new Set();
    for (const row of accountsTabData.data || []) {
      if (row?.bookkeeper) {
        values.add(String(row.bookkeeper).trim());
      }
    }
    return Array.from(values).sort();
  }, [accountsTabData.data]);

  const managerBookkeeperFilterPopover = useCallback(
    (fieldName, options, context) => {
      const asRecords = options.map((name, index) => ({ id: `${fieldName}-${index}`, name }));
      return (
        <BookkeeperMultiSelectPopover
          value={context.filters[fieldName] || ""}
          onValueChange={(newValue) =>
            context.setFilters((prev) => ({ ...prev, [fieldName]: newValue }))
          }
          bookkeepers={asRecords}
          loadingBookkeepers={false}
          onClose={context.handleFilterMenuClose}
          onSearch={() => {
            context.handleFilterChange(fieldName, context.filters[fieldName], true);
            context.handleFilterMenuClose();
          }}
          onClear={() => {
            context.setFilters((prev) => ({ ...prev, [fieldName]: "" }));
            context.handleFilterChange(fieldName, "", true);
            context.handleFilterMenuClose();
          }}
        />
      );
    },
    []
  );

  const filterPopoverByField = useMemo(
    () => ({
      primaryContact: (context) => (
        <MultiSelectSortFilterPopover
          field="primaryContact"
          headerName="Primary Contact"
          rows={accountsTabData.data}
          filterValue={context.filters.primaryContact || ""}
          sortField={context.sortConfig.field}
          sortDirection={context.sortConfig.direction}
          onFilterValueChange={(nextValue) =>
            context.setFilters((prev) => ({ ...prev, primaryContact: nextValue }))
          }
          onApplyFilter={(nextValue) =>
            context.handleFilterChange("primaryContact", nextValue, true)
          }
          onSortChange={context.handleSort}
          onClose={context.handleFilterMenuClose}
        />
      ),
      accountType: (context) => (
        <MultiSelectSortFilterPopover
          field="accountType"
          headerName="Account Type"
          rows={accountsTabData.data}
          options={ACCOUNT_TYPE_FILTER_OPTIONS}
          filterValue={context.filters.accountType || ""}
          showSort={false}
          applyButtonLabel="Filter"
          sortField={context.sortConfig.field}
          sortDirection={context.sortConfig.direction}
          onFilterValueChange={(nextValue) =>
            context.setFilters((prev) => ({ ...prev, accountType: nextValue }))
          }
          onApplyFilter={(nextValue) =>
            context.handleFilterChange("accountType", nextValue, true)
          }
          onSortChange={context.handleSort}
          onClose={context.handleFilterMenuClose}
        />
      ),
      manager: (context) => managerBookkeeperFilterPopover("manager", managerOptions, context),
      bookkeeper: (context) =>
        managerBookkeeperFilterPopover("bookkeeper", bookkeeperOptions, context),
    }),
    [
      ACCOUNT_TYPE_FILTER_OPTIONS,
      accountsTabData.data,
      managerBookkeeperFilterPopover,
      managerOptions,
      bookkeeperOptions,
    ]
  );

  const { renderCellContent } = useMemo(
    () =>
      createManagementCellRenderer({
        onNoteClick: undefined,
      }),
    []
  );

  const handleCreate = useCallback(() => {
    openDialogManager("create");
  }, [openDialogManager]);

  const handleEdit = useCallback(
    (row) => {
      openDialogManager("edit", row);
    },
    [openDialogManager]
  );

  const handleExportSelected = useCallback(async () => {
    const selectedCount = selectedRows.length;

    try {
      const response = await exportCustomers(selectedCount > 0 ? selectedRows : null);

      const link = document.createElement("a");
      link.href = response.data.file;
      link.setAttribute("download", response.data.name);
      document.body.appendChild(link);
      link.click();
      link.remove();

      notify({
        open: true,
        message:
          selectedCount > 0
            ? `${selectedCount} selected accounts exported successfully!`
            : "All accounts exported successfully!",
        severity: "success",
      });

      setSelectedRows([]);
      onSortChange?.(null, null);
    } catch {
      notify({
        open: true,
        message: "Failed to export accounts.",
        severity: "error",
      });
    }
  }, [notify, onSortChange, selectedRows, setSelectedRows]);

  const deleteRow = useCallback(
    async (row) => {
      const customerId = row?.originalCustomer?.id || row?.id;
      if (!customerId) {
        throw new Error("Missing customer id for delete");
      }

      await deleteCustomer(customerId);
      await accountsTabData.refresh?.();

      notify({
        open: true,
        message: "Account deleted successfully!",
        severity: "success",
      });
    },
    [accountsTabData, notify]
  );

  const handleDeleteRequest = useCallback((row) => {
    if (!row) return;
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    if (deletingRow) return;
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  }, [deletingRow]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!rowToDelete || deletingRow) return;

    setDeletingRow(true);
    try {
      await deleteRow(rowToDelete);
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      setSelectedRows([]);
    } catch {
      notify({
        open: true,
        message: "Failed to delete account. Please try again.",
        severity: "error",
      });
    } finally {
      setDeletingRow(false);
    }
  }, [deleteRow, deletingRow, notify, rowToDelete, setSelectedRows]);

  const renderRowActionsMenu = useCallback(
    ({ anchorEl, onClose, row, onDeleteRequest }) => (
      <RowActionsMenu
        anchorEl={anchorEl}
        onClose={onClose}
        isUserTable={false}
        row={row}
        onEdit={handleEdit}
        onView={(rowData) => {
          const customerId = rowData?.originalCustomer?.id || rowData?.id;
          if (customerId) {
            window.open(`/client/${customerId}`, "_blank");
          }
        }}
        onDeleteRequest={onDeleteRequest}
      />
    ),
    [handleEdit]
  );

  const renderTopToolbar = useCallback(() => {
    const active = accountsTabData.activeManagerFilter;
    if (!active || !active.length) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 3, py: 2, backgroundColor: "#f9fafb" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Manager:
          </Typography>

          {active.map((managerName, index) => (
            <Chip
              key={index}
              label={managerName}
              onDelete={() => {
                const updatedManagers = active.filter((_, i) => i !== index);
                accountsTabData.handleManagerFilterChange(updatedManagers.length > 0 ? updatedManagers : null);
                setSelectedRows([]);
              }}
            />
          ))}

          <Button
            startIcon={<Delete />}
            onClick={() => {
              accountsTabData.handleManagerFilterChange(null);
              setSelectedRows([]);
            }}
            size="small"
            sx={{ color: "#ef4444", textTransform: "none", fontWeight: 500 }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
    );
  }, [accountsTabData, setSelectedRows]);

  const renderSelectionToolbar = useCallback(() => {
    if (!selectedRows.length) {
      return null;
    }

    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{selectedRows.length}</Box> Accounts Selected
        </Typography>

        <Button
          startIcon={<EditNote />}
          onClick={() => setBulkEditDialogOpen(true)}
          size="small"
          variant="outlined"
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Bulk Edit
        </Button>

        <Button
          startIcon={<UploadFileOutlined />}
          onClick={handleExportSelected}
          size="small"
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Export
        </Button>
      </>
    );
  }, [handleExportSelected, selectedRows.length]);

  const contract = {
    tableProps: {
      columns,
      rows: accountsTabData.data,
      loading: accountsTabData.loading,
      error: accountsTabData.error,
      emptyMessage: "No accounts found",
      searchTerm: accountsTabData.searchTerm,
      page: accountsTabData.page,
      serverSidePagination: true,
      hasNextPage: !!accountsTabData.nextCursor,
      hasPrevPage: !!accountsTabData.prevCursor,
      onNextPage: accountsTabData.handleNextPage,
      onPreviousPage: accountsTabData.handlePreviousPage,
      onFiltersChange: accountsTabData.handleFiltersChange,
      filterPopoverByField,
      onDeleteRequest: handleDeleteRequest,
      renderCellContent,
      renderRowActionsMenu,
    },
    toolbar: {
      searchValue: accountsTabData.searchTerm || "",
      onSearchChange: accountsTabData.handleSearchChange,
      createAction: handleCreate,
      renderTop: renderTopToolbar,
      renderSelection: renderSelectionToolbar,
    },
    dialogs: {
      render: () => (
        <>
          <AccountBulkEditDialog
            open={bulkEditDialogOpen}
            onClose={() => setBulkEditDialogOpen(false)}
            selectedAccounts={selectedRows}
            onSubmit={() => {
              setSelectedRows([]);
              onSortChange?.(null, null);
            }}
            onShowSuccess={(message, severity = "success", autoHideDuration) =>
              notify({ open: true, message, severity, autoHideDuration })
            }
          />
          <ConfirmDeleteDialog
            open={deleteDialogOpen}
            itemLabel={rowToDelete?.accountName || rowToDelete?.name || rowToDelete?.originalCustomer?.name}
            deleting={deletingRow}
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
          />
        </>
      ),
    },
    commands: {
      refresh: accountsTabData.refresh,
      exportSelected: handleExportSelected,
      clearSelection: () => setSelectedRows([]),
      deleteRow,
    },
    meta: {
      sectionId: "accounts",
      capabilities: {
        bulkEdit: true,
        managerOptions: managerOptions.length > 0,
        bookkeeperOptions: bookkeeperOptions.length > 0,
      },
    },
  };

  assertSectionControllerContract("accounts", contract);
  return contract;
}
